import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Grid,
  Paper,
  Chip,
  Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import LanguageIcon from '@mui/icons-material/Language';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PhonelinkSetupIcon from '@mui/icons-material/PhonelinkSetup';

interface CihazBilgileriModalProps {
  open: boolean;
  onClose: () => void;
  deviceInfo: any | null; // Cihaz bilgisi
  actionType: 'giris' | 'cikis'; // Giriş mi çıkış mı (artık kullanılmayacak ama tipi koruyoruz)
}

const CihazBilgileriModal: React.FC<CihazBilgileriModalProps> = ({
  open,
  onClose,
  deviceInfo,
  actionType // Bu parametreyi kullanmayacağız artık
}) => {
  // Debug: Cihaz bilgilerini kontrol et
  useEffect(() => {
    if (open && deviceInfo) {
      console.log('Modal açıldı, cihaz bilgileri:', deviceInfo);
    }
  }, [open, deviceInfo]);

  // Cihaz bilgisi yoksa
  if (!deviceInfo) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Cihaz Bilgileri</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography align="center" py={4}>Cihaz bilgisi bulunamadı.</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  // Android için bilgileri düzenle
  const formatAndroidInfo = (info: any) => {
    console.log('Android bilgileri formatlanıyor:', info);
    return {
      model: `${info.brand || ''} ${info.model || ''}`.trim() || 'Bilinmiyor',
      detailList: [
        { label: 'Marka', value: info.brand || 'Bilinmiyor', icon: <SmartphoneIcon /> },
        { label: 'Model', value: info.model || 'Bilinmiyor', icon: <SmartphoneIcon /> },
        { label: 'Android Sürümü', value: info.version?.release || 'Bilinmiyor', icon: <SystemUpdateAltIcon /> },
        { label: 'SDK Sürümü', value: info.version?.sdkInt || 'Bilinmiyor', icon: <MemoryIcon /> },
        { label: 'Cihaz ID', value: info.id || 'Bilinmiyor', icon: <PhonelinkSetupIcon /> },
        { label: 'Üretici', value: info.manufacturer || 'Bilinmiyor', icon: <MemoryIcon /> },
        { label: 'Donanım', value: info.hardware || 'Bilinmiyor', icon: <MemoryIcon /> },
        { label: 'İşletim Sistemi', value: 'Android', icon: <LanguageIcon /> },
        { label: 'Ürün', value: info.product || 'Bilinmiyor', icon: <SmartphoneIcon /> },
        { label: 'Host', value: info.host || 'Bilinmiyor', icon: <StorageIcon /> },
        { label: 'Bootloader', value: info.bootloader || 'Bilinmiyor', icon: <StorageIcon /> },
        { label: 'Display', value: info.display || 'Bilinmiyor', icon: <PhonelinkSetupIcon /> },
        { label: 'Fingerprint', value: info.fingerprint || 'Bilinmiyor', icon: <CheckCircleOutlineIcon /> },
      ]
    };
  };

  // iOS için bilgileri düzenle
  const formatIosInfo = (info: any) => {
    console.log('iOS bilgileri formatlanıyor:', info);
    return {
      model: info.model || 'iPhone',
      detailList: [
        { label: 'İsim', value: info.name || 'Bilinmiyor', icon: <SmartphoneIcon /> },
        { label: 'Model', value: info.model || 'Bilinmiyor', icon: <SmartphoneIcon /> },
        { label: 'Sistem Adı', value: info.systemName || 'iOS', icon: <LanguageIcon /> },
        { label: 'Sistem Sürümü', value: info.systemVersion || 'Bilinmiyor', icon: <SystemUpdateAltIcon /> },
        { label: 'İşletim Sistemi', value: 'iOS', icon: <LanguageIcon /> },
        { label: 'Cihaz ID', value: info.identifierForVendor || 'Bilinmiyor', icon: <PhonelinkSetupIcon /> },
        { label: 'Yerel Bilgiler', value: info.localizedModel || 'Bilinmiyor', icon: <SmartphoneIcon /> },
        { label: 'Kullanılabilir Bellek', value: info.isAvailable ? 'Evet' : 'Hayır', icon: <StorageIcon /> },
      ]
    };
  };

  // Web tarayıcısı için bilgileri düzenle
  const formatWebInfo = (info: any) => {
    console.log('Web bilgileri formatlanıyor:', info);
    return {
      model: 'Web Tarayıcısı',
      detailList: [
        { label: 'Tarayıcı', value: info.userAgent || 'Bilinmiyor', icon: <LanguageIcon /> },
        { label: 'Platform', value: info.platform || 'Bilinmiyor', icon: <MemoryIcon /> }
      ]
    };
  };

  // Cihaz tipine göre bilgileri hazırla
  let formattedInfo;
  let deviceType = deviceInfo.deviceType || 'Bilinmiyor';

  console.log('Cihaz tipi:', deviceType);

  if (deviceInfo.androidInfo) {
    deviceType = 'Android';
    formattedInfo = formatAndroidInfo(deviceInfo.androidInfo);
  } else if (deviceInfo.iosInfo) {
    deviceType = 'iOS';
    formattedInfo = formatIosInfo(deviceInfo.iosInfo);
  } else if (deviceInfo.webInfo) {
    deviceType = 'Web';
    formattedInfo = formatWebInfo(deviceInfo.webInfo);
  } else {
    // Eski format cihaz bilgisi için (doğrudan özellikler içeren)
    console.log('Düz cihaz bilgisi formatlanıyor:', deviceInfo);
    formattedInfo = { 
      model: `${deviceInfo.brand || ''} ${deviceInfo.model || ''}`.trim() || 'Bilinmiyor',
      detailList: [
        { label: 'Marka', value: deviceInfo.brand || 'Bilinmiyor', icon: <SmartphoneIcon /> },
        { label: 'Model', value: deviceInfo.model || 'Bilinmiyor', icon: <SmartphoneIcon /> },
        { label: 'Üretici', value: deviceInfo.manufacturer || 'Bilinmiyor', icon: <MemoryIcon /> },
        { label: 'Cihaz ID', value: deviceInfo.deviceId || 'Bilinmiyor', icon: <PhonelinkSetupIcon /> },
        { label: 'Cihaz Tipi', value: deviceInfo.deviceType || 'Bilinmiyor', icon: <LanguageIcon /> },
      ]
    };
  }

  // Modal başlığı - giriş/çıkış durumuna göre
  const titleText = actionType === 'giris' ? 'Giriş Yapılan Cihaz Bilgileri' : 'Çıkış Yapılan Cihaz Bilgileri';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        borderBottom: '1px solid', 
        borderColor: 'divider', 
        bgcolor: actionType === 'giris' ? 'primary.50' : 'secondary.50',
        color: actionType === 'giris' ? 'primary.main' : 'secondary.main'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SmartphoneIcon sx={{ mr: 1, color: actionType === 'giris' ? 'primary.main' : 'secondary.main' }} />
            <Typography variant="h6">{titleText}</Typography>
          </Box>
          <Box>
            <Chip 
              label={deviceType} 
              color={
                deviceType === 'Android' ? 'success' : 
                deviceType === 'iOS' ? 'info' : 
                deviceType === 'Web' ? 'warning' : 'default'
              } 
              size="small" 
              sx={{ mr: 1 }}
            />
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.default', pt: 3 }}>
        <Box sx={{ p: 1 }}>
          <Box sx={{ 
            mb: 3, 
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: actionType === 'giris' ? 'primary.50' : 'secondary.50', 
            p: 2, 
            borderRadius: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <Avatar 
              sx={{ 
                width: 60, 
                height: 60, 
                mr: 2,
                bgcolor: actionType === 'giris' ? 'primary.main' : 'secondary.main',
              }}
            >
              <SmartphoneIcon sx={{ fontSize: '2.5rem' }} />
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 'medium' }}>
                {actionType === 'giris' ? 'Personel giriş yaptığında kullandığı cihaz:' : 'Personel çıkış yaptığında kullandığı cihaz:'}
              </Typography>
              <Typography variant="h5" color={actionType === 'giris' ? 'primary' : 'secondary'} fontWeight="bold">
                {formattedInfo.model}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {deviceType === 'Android' ? 'Android İşletim Sistemi' : 
                 deviceType === 'iOS' ? 'iOS İşletim Sistemi' : 
                 deviceType === 'Web' ? 'Web Tarayıcısı' : 'Bilinmeyen Cihaz Tipi'}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
              Cihaz Detayları
            </Typography>
            <Chip 
              label={actionType === 'giris' ? 'Giriş Cihazı' : 'Çıkış Cihazı'} 
              color={actionType === 'giris' ? 'primary' : 'secondary'} 
              size="small"
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
          
          <Grid container spacing={2}>
            {formattedInfo.detailList.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderLeft: '4px solid',
                    borderColor: actionType === 'giris' ? 'primary.main' : 'secondary.main',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: actionType === 'giris' ? 'primary.main' : 'secondary.main' }}>
                    {React.cloneElement(item.icon, { fontSize: 'small' })}
                    <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 'bold' }}>
                      {item.label}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    bgcolor: 'background.paper', 
                    p: 1, 
                    borderRadius: 1, 
                    mt: 'auto', 
                    border: '1px solid',
                    borderColor: 'divider' 
                  }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CihazBilgileriModal; 